import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, MapPin, Trophy, Sparkles } from "lucide-react";
import Marquee from "react-fast-marquee";
import NextClueChip from "@/components/NextClueChip";

export default function GoldenDropsPage() {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/drops/golden");
      setDrops(data.drops || []);
      setLoading(false);
    })();
  }, []);

  const liveGolden = drops.find((d) => d.status === "live");

  return (
    <div className="py-8 space-y-8">
      {liveGolden && (
        <div
          className="border -mx-4 md:-mx-8"
          style={{
            borderColor: "#D7A62A",
            background:
              "linear-gradient(90deg, rgba(215,166,42,0.18), rgba(15,15,15,0.4), rgba(215,166,42,0.18))",
          }}
          data-testid="golden-live-banner"
        >
          <Marquee gradient={false} speed={45}>
            <span className="font-display text-4xl md:text-6xl uppercase py-3 px-8 tracking-tight">
              <span style={{ color: "#D7A62A" }}>★ GOLDEN DROP LIVE IN {liveGolden.city}</span>
              <span className="text-[#F5F2E8]/40"> // </span>
              <span style={{ color: "#F5F2E8" }}>${liveGolden.amount}</span>
              <span className="text-[#F5F2E8]/40"> // </span>
              <span style={{ color: "#D7A62A" }}>SPONSORED BY {liveGolden.sponsor?.business_name?.toUpperCase() || "PREMIUM PARTNER"}</span>
              <span className="text-[#F5F2E8]/40"> // </span>
            </span>
          </Marquee>
        </div>
      )}

      <div>
        <div className="overline flex items-center gap-2">
          <Sparkles size={14} /> Premium-sponsored, rare, and bigger
        </div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
          Golden <span style={{ color: "#D7A62A" }}>drops</span>
        </h1>
        <p className="mt-3 text-[#F5F2E8]/70 max-w-xl">
          Our rarest hunts. Funded by City Takeover sponsors. Bigger prizes. Bigger stories. Find one and earn the{" "}
          <span className="font-display uppercase" style={{ color: "#D7A62A" }}>Golden Drop Winner</span> badge for life.
        </p>
      </div>

      {loading ? (
        <div className="text-[#F5F2E8]/50">Loading...</div>
      ) : drops.length === 0 ? (
        <div className="loot-card-flat p-12 text-center">
          <Crown className="mx-auto mb-3" size={48} style={{ color: "#D7A62A" }} />
          <div className="font-display uppercase text-2xl">No golden drops yet</div>
          <p className="text-[#F5F2E8]/60 mt-2 max-w-sm mx-auto">
            Be the first premium sponsor. Fund a Golden Drop via the City Takeover tier and own the city for a week.
          </p>
          <Link to="/sponsor-apply" className="btn-gold inline-block mt-6" data-testid="golden-sponsor-cta">
            Sponsor a Golden Drop →
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {drops.map((d) => (
            <Link
              to={`/drops/${d.id}`}
              key={d.id}
              className="loot-card p-6 block relative overflow-hidden"
              style={{
                borderColor: "#D7A62A",
                background:
                  "linear-gradient(135deg, rgba(215,166,42,0.08), rgba(15,15,15,1) 50%)",
              }}
              data-testid={`golden-card-${d.id}`}
            >
              <Crown
                size={120}
                aria-hidden="true"
                className="absolute -right-8 -top-8 opacity-10"
                style={{ color: "#D7A62A" }}
              />
              <div className="flex items-center justify-between relative">
                <span
                  className="tag-pill flex items-center gap-1"
                  style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                >
                  <Crown size={10} /> Golden · {d.status}
                </span>
                <span className="font-display text-2xl" style={{ color: "#D7A62A" }}>
                  ${d.amount}
                </span>
              </div>
              <h3 className="font-display text-3xl uppercase mt-4 leading-tight tracking-tight">
                {d.title}
              </h3>
              <div className="mt-2 flex items-center gap-2 text-sm text-[#F5F2E8]/70">
                <MapPin size={14} /> {d.city}
              </div>
              <div className="mt-2">
                <NextClueChip drop={d} />
              </div>
              <div className="mt-4 font-mono text-xs text-[#F5F2E8]/60">
                {d.sponsor ? `Premium sponsor · ${d.sponsor.business_name}` : "Premium sponsored"}
              </div>
              {d.winner && d.status !== "live" && (
                <div
                  className="mt-4 border-t pt-3 flex items-center gap-2 text-sm"
                  style={{ borderColor: "rgba(245,242,232,0.1)" }}
                >
                  <Trophy size={14} style={{ color: "#D7A62A" }} />
                  <span className="font-display uppercase">
                    Claimed by @{d.winner.username}
                  </span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
